import { create } from "zustand";
import { locationApi } from "@/lib/api";

const KEY = "trata_web_location";
const SAVED_KEY = "trata_web_saved_locations";

function loadInitial() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return { city: null, area: null, pincode: null, label: null, coords: null };
}

function loadSaved() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return [];
}

const initial = loadInitial();

function persistActive(next) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ city: next.city, area: next.area, pincode: next.pincode, label: next.label, coords: next.coords }));
  } catch (e) {
    // ignore
  }
}

export const useLocationStore = create((set, get) => ({
  city: initial.city,
  area: initial.area,
  pincode: initial.pincode,
  label: initial.label,
  coords: initial.coords,
  saved: loadSaved(),
  detecting: false,
  error: null, // 'denied' | 'unavailable' | 'unsupported' | null

  // Fetch the device's realtime GPS position and reverse-geocode it to a city/area.
  detect: () =>
    new Promise((resolve) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        set({ error: "unsupported", detecting: false });
        resolve({ ok: false, error: "unsupported" });
        return;
      }
      set({ detecting: true, error: null });
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const res = await locationApi.reverseGeocode(latitude, longitude);
            const d = res.data || {};
            const city = d.city || d.area || null;
            const label =
              d.area && d.city && d.area !== d.city
                ? `${d.area}, ${d.city}`
                : city || `${latitude.toFixed(3)}, ${longitude.toFixed(3)}`;
            const next = {
              city,
              area: d.area || null,
              pincode: d.pincode || null,
              label,
              coords: { latitude, longitude },
              detecting: false,
              error: null,
            };
            set(next);
            persistActive(next);
            resolve({ ok: true, label });
          } catch (e) {
            set({ detecting: false, error: "unavailable" });
            resolve({ ok: false, error: "unavailable" });
          }
        },
        (err) => {
          set({ detecting: false, error: err && err.code === 1 ? "denied" : "unavailable" });
          resolve({ ok: false, error: err && err.code === 1 ? "denied" : "unavailable" });
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
      );
    }),

  // Set the active location from a picked/saved entry.
  setActive: (loc) => {
    const next = {
      city: loc.city || null,
      area: loc.area || null,
      pincode: loc.pincode || null,
      label: loc.label || loc.name || loc.city || null,
      coords: loc.coords || (loc.latitude != null ? { latitude: loc.latitude, longitude: loc.longitude } : null),
      detecting: false,
      error: null,
    };
    set(next);
    persistActive(next);
  },

  // Save a named location (dedupes by name). Returns the saved list.
  addSaved: (entry) => {
    const id = entry.id || Date.now().toString();
    const item = { id, ...entry };
    const list = [item, ...get().saved.filter((s) => s.name !== entry.name)].slice(0, 12);
    set({ saved: list });
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
    return list;
  },

  removeSaved: (id) => {
    const list = get().saved.filter((s) => s.id !== id);
    set({ saved: list });
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
    return list;
  },

  // Best label to show, falling back to a provided profile city.
  displayLabel: (fallbackCity) => {
    const s = get();
    return s.label || s.city || fallbackCity || null;
  },
}));
